import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, FolderOpen, Trash2, Upload, Archive, ArchiveRestore, Eye, EyeOff } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import BulkImportModal from '@/components/BulkImportModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Accounts = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [selectedAccountForImport, setSelectedAccountForImport] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#4F46E5'
  });

  useEffect(() => {
    fetchAccounts();
  }, [showArchived]);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/accounts?include_archived=${showArchived}`);
      setAccounts(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      toast.error('Failed to load accounts');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/accounts`, formData);
      toast.success('Account created successfully');
      setDialogOpen(false);
      setFormData({ name: '', description: '', color: '#4F46E5' });
      fetchAccounts();
    } catch (error) {
      console.error('Error creating account:', error);
      toast.error('Failed to create account');
    }
  };

  const handleDelete = async (accountId) => {
    if (!window.confirm('Delete this account? All tasks will remain but lose account association.')) return;
    try {
      await axios.delete(`${API}/accounts/${accountId}`);
      toast.success('Account deleted');
      fetchAccounts();
      window.dispatchEvent(new Event('refreshDashboard'));
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Failed to delete account');
    }
  };

  const openImportModal = (account) => {
    setSelectedAccountForImport(account);
    setImportModalOpen(true);
  };

  const handleImportSuccess = () => {
    fetchAccounts();
    window.dispatchEvent(new Event('refreshDashboard'));
  };

  const handleArchive = async (accountId) => {
    if (!window.confirm('Are you sure you want to archive this account?')) return;
    try {
      await axios.post(`${API}/accounts/${accountId}/archive`);
      toast.success('Account archived');
      fetchAccounts();
    } catch (error) {
      toast.error('Failed to archive account');
    }
  };

  const handleUnarchive = async (accountId) => {
    try {
      await axios.post(`${API}/accounts/${accountId}/unarchive`);
      toast.success('Account unarchived');
      fetchAccounts();
    } catch (error) {
      toast.error('Failed to unarchive account');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="p-8 space-y-6"
      data-testid="accounts-page"
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold tracking-tight">Accounts</h1>
          <p className="text-muted-foreground mt-1">Manage your client accounts and their tasks</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowArchived(!showArchived)}
            className={showArchived ? 'bg-amber-50 border-amber-300' : ''}
            data-testid="toggle-archived-btn"
          >
            {showArchived ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
            {showArchived ? 'Hide Archived' : 'Show Archived'}
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-account-btn" className="bg-primary hover:bg-primary/90">
                <Plus className="h-4 w-4 mr-2" />
                Add Account
              </Button>
            </DialogTrigger>
          <DialogContent className="bg-white" data-testid="account-dialog">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">Create New Account</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="name" className="text-sm font-semibold">Account Name *</Label>
                <Input
                  id="name"
                  data-testid="account-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="mt-1.5 bg-white"
                  placeholder="e.g., BharatREVOLT"
                />
              </div>
              <div>
                <Label htmlFor="description" className="text-sm font-semibold">Description</Label>
                <Textarea
                  id="description"
                  data-testid="account-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="mt-1.5 bg-white"
                  placeholder="Brief description of the account"
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="color" className="text-sm font-semibold">Color Tag</Label>
                <Input
                  id="color"
                  type="color"
                  data-testid="account-color-input"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="mt-1.5 h-12 bg-white"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90">
                  Create Account
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {accounts.map((account) => (
          <motion.div
            key={account.id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
          >
            <Card
              className={`p-6 hover:shadow-lg transition-shadow border-l-4 bg-white relative group ${account.archived ? 'opacity-60' : ''}`}
              style={{ borderLeftColor: account.color }}
              data-testid={`account-card-${account.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {/* Archived badge */}
              {account.archived && (
                <div className="absolute top-3 left-3 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">
                  Archived
                </div>
              )}
              
              {/* Action buttons */}
              <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-all">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openImportModal(account);
                  }}
                  className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-all"
                  title="Import tasks from spreadsheet"
                  data-testid={`import-tasks-${account.id}`}
                >
                  <Upload className="h-4 w-4" />
                </button>
                {account.archived ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUnarchive(account.id);
                    }}
                    className="p-2 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 transition-all"
                    title="Unarchive account"
                    data-testid={`unarchive-account-${account.id}`}
                  >
                    <ArchiveRestore className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleArchive(account.id);
                    }}
                    className="p-2 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 transition-all"
                    title="Archive account"
                    data-testid={`archive-account-${account.id}`}
                  >
                    <Archive className="h-4 w-4" />
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(account.id);
                  }}
                  className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-all"
                  data-testid={`delete-account-${account.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              
              <div 
                className="flex items-start gap-4 cursor-pointer"
                onClick={() => navigate(`/accounts/${account.id}`)}
              >
                <div
                  className="h-12 w-12 rounded-lg flex items-center justify-center text-white font-bold text-xl"
                  style={{ backgroundColor: account.color }}
                >
                  {account.name.charAt(0)}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold">{account.name}</h3>
                  {account.description && (
                    <p className="text-sm text-muted-foreground mt-1">{account.description}</p>
                  )}
                  <div className="flex items-center gap-2 mt-3">
                    <FolderOpen className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">
                      {account.taskCount} task{account.taskCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      {accounts.length === 0 && (
        <Card className="p-12 text-center bg-white">
          <FolderOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No accounts yet</p>
          <p className="text-sm text-muted-foreground mt-2">Create your first account to get started</p>
        </Card>
      )}

      {/* Bulk Import Modal */}
      {selectedAccountForImport && (
        <BulkImportModal
          open={importModalOpen}
          onOpenChange={setImportModalOpen}
          accountId={selectedAccountForImport.id}
          accountName={selectedAccountForImport.name}
          onSuccess={handleImportSuccess}
        />
      )}
    </motion.div>
  );
};

export default Accounts;
